import React, { useState, useEffect } from 'react';
import { 
  Server, Zap, Flame, Battery, Cpu, Hexagon, Activity,
  Wind, Droplets, Droplet, Box, Layers, Shield, Sun, Fan
} from 'lucide-react';
import { BrandLogo } from '../auth/BrandLogo';

interface Props {
  phase: 'auth' | 'data' | 'ready' | 'idle';
  variant?: 'fullscreen' | 'inline';
  onEscape?: () => void;
}

type Config = {
  id: string;
  power: { id: string; name: string; status: string; type: 'nuclear' | 'gas' | 'renew' };
  cooling: { id: string; name: string; type: 'wet' | 'dry' | 'hybrid' };
  dc: { id: string; name: string; type: 'hyperscale' | 'colocation' | 'edge'; lines: number };
}

const CONFIGURATIONS: Config[] = [
  {
    id: 'c1',
    power: { id: 'large-nuclear', name: 'Large Nuclear (GW)', status: 'Core Critical', type: 'nuclear' },
    cooling: { id: 'wet-evap', name: 'Evaporative Cooling', type: 'wet' },
    dc: { id: 'hyperscale', name: 'Hyperscale DC', type: 'hyperscale', lines: 5 }
  },
  {
    id: 'c2',
    power: { id: 'smr', name: 'SMR Reactor', status: 'Thermal Optimal', type: 'nuclear' },
    cooling: { id: 'hybrid', name: 'Hybrid Cooling Loop', type: 'hybrid' },
    dc: { id: 'colocation', name: 'Large Colocation', type: 'colocation', lines: 3 }
  },
  {
    id: 'c3',
    power: { id: 'recip-gas', name: 'Reciprocating Gas', status: 'Synchronized', type: 'gas' },
    cooling: { id: 'dry-air', name: 'Radiator Dry Cooled', type: 'dry' },
    dc: { id: 'enterprise', name: 'Enterprise DC', type: 'colocation', lines: 2 }
  },
  {
    id: 'c4',
    power: { id: 'microreactor', name: 'Microreactor', status: 'Fission Active', type: 'nuclear' },
    cooling: { id: 'dry-air', name: 'Ambient Air Cooled', type: 'dry' },
    dc: { id: 'edge', name: 'Edge Inference', type: 'edge', lines: 1 }
  },
  {
    id: 'c5',
    power: { id: 'ccgt', name: 'Combined Cycle Gas', status: 'Turbines Spooled', type: 'gas' },
    cooling: { id: 'wet-evap', name: 'Evaporative Cooling', type: 'wet' },
    dc: { id: 'hyperscale', name: 'Hyperscale DC', type: 'hyperscale', lines: 4 }
  },
  {
    id: 'c6',
    power: { id: 'solar-bess', name: 'Solar + BESS Firming', status: 'Discharging', type: 'renew' },
    cooling: { id: 'dry-air', name: 'Ambient Air Cooled', type: 'dry' },
    dc: { id: 'edge', name: 'Local Edge Pod', type: 'edge', lines: 1 }
  },
  {
    id: 'c7',
    power: { id: 'fuel-cell', name: 'Hydrogen Fuel Cell', status: 'Stack Energized', type: 'gas' },
    cooling: { id: 'liquid-dlc', name: 'Direct Liquid Cooling', type: 'hybrid' },
    dc: { id: 'hyperscale', name: 'AI Training Cluster', type: 'hyperscale', lines: 5 }
  },
  {
    id: 'c8',
    power: { id: 'wind-bess', name: 'Wind + BESS', status: 'Grid-Forming', type: 'renew' },
    cooling: { id: 'dry-closed', name: 'Closed-Loop Dry', type: 'dry' },
    dc: { id: 'edge', name: 'Micro Edge Node', type: 'edge', lines: 1 }
  },
  {
    id: 'c9',
    power: { id: 'large-nuclear', name: 'Large Nuclear (GW)', status: 'Baseload Steady', type: 'nuclear' },
    cooling: { id: 'hybrid-adiabatic', name: 'Adiabatic Hybrid', type: 'hybrid' },
    dc: { id: 'colocation', name: 'Regional Colocation', type: 'colocation', lines: 3 }
  },
  {
    id: 'c10',
    power: { id: 'ccgt', name: 'Combined Cycle Gas', status: 'Peaking Online', type: 'gas' },
    cooling: { id: 'dry-closed', name: 'Closed-Loop Dry', type: 'dry' },
    dc: { id: 'enterprise', name: 'Enterprise DC', type: 'colocation', lines: 2 }
  },
  {
    id: 'c11',
    power: { id: 'smr', name: 'SMR Reactor', status: 'Load Following', type: 'nuclear' },
    cooling: { id: 'wet-tower', name: 'Cooling Tower', type: 'wet' },
    dc: { id: 'hyperscale', name: 'GPU Megacluster', type: 'hyperscale', lines: 4 }
  },
  {
    id: 'c12',
    power: { id: 'microreactor', name: 'Microreactor', status: 'Islanded', type: 'nuclear' },
    cooling: { id: 'liquid-dlc', name: 'Direct Liquid Cooling', type: 'hybrid' },
    dc: { id: 'edge', name: 'Inference Pod', type: 'edge', lines: 1 }
  }
];

export function UnifiedLoader({ phase, variant = 'fullscreen', onEscape }: Props) {
  const [show, setShow] = useState(true);
  const [exitFade, setExitFade] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const [configs, setConfigs] = useState<Config[]>(CONFIGURATIONS);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    // Shuffle on mount
    setConfigs([...CONFIGURATIONS].sort(() => Math.random() - 0.5));
  }, []);

  useEffect(() => {
    // Cycle every 7 seconds (1/2 speed of original 3.5s) to stay on each combination longer
    const t = setInterval(() => {
      setIdx((prev) => (prev + 1) % configs.length);
    }, 7000);
    return () => clearInterval(t);
  }, [configs.length]);

  useEffect(() => {
    if (phase === 'ready') {
      setExitFade(true);
      const t = setTimeout(() => setShow(false), 800);
      return () => clearTimeout(t);
    }
    if (!dismissed) {
      setExitFade(false);
    }
  }, [phase, dismissed]);

  useEffect(() => {
    if (variant === 'inline' || phase !== 'data' || dismissed) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDismissed(true);
        setShow(false);
        onEscape?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [variant, phase, dismissed, onEscape]);

  if (phase === 'ready' || !show || dismissed) return null;

  const current = configs[idx];

  if (variant === 'inline') {
    return (
      <div className="relative flex flex-col items-center justify-center font-mono overflow-hidden w-full h-48 py-4">
        <div className="relative z-10 flex items-center justify-between px-4 sm:px-10 w-full max-w-xl scale-75 sm:scale-100 origin-center">
          <UnifiedAnimation current={current} />
        </div>
        <LoaderStyles />
      </div>
    );
  }

  return (
    <div
      data-testid="unified-loader"
      className={`fixed inset-0 z-[9999] bg-background flex flex-col items-center justify-center font-mono overflow-hidden transition-opacity duration-700 ${exitFade ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
    >
      
      {/* Background Matrix */}
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none flex items-center justify-center overflow-hidden">
        <div 
          className="absolute w-[200vw] h-[200vh]"
          style={{
            backgroundImage: 'radial-gradient(circle at center, var(--primary) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            animation: 'gridDrift 40s linear infinite',
            opacity: 0.15
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,var(--background)_70%)]" />
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center space-y-24 max-w-4xl w-full px-6">
        
        {/* Brand Logo */}
        <div className="relative">
          <div className="absolute -inset-12 bg-primary/10 blur-3xl rounded-full" />
          <div className="animate-pulse duration-[4000ms] relative">
            <BrandLogo height={48} />
          </div>
        </div>

        {/* Dynamic Animation Area */}
        <div className="relative w-full h-48 flex items-center justify-between px-10">
          <UnifiedAnimation current={current} />
        </div>

        {/* Loading Micro-copy */}
        <div className="flex flex-col items-center justify-center relative mt-16 gap-4">
          <div className="h-8 flex items-center justify-center relative">
            <div className="absolute -inset-4 bg-primary/5 blur-2xl rounded-full" />
            <span 
              className="text-sm font-bold uppercase tracking-[0.4em] bg-clip-text text-transparent bg-[linear-gradient(110deg,var(--text-muted),var(--text-muted),var(--primary),var(--text-primary),var(--primary),var(--text-muted),var(--text-muted))] relative z-10"
              style={{ backgroundSize: '200% auto', animation: 'shine 6s linear infinite' }}
            >
              {phase === 'auth' && 'Initializing Core Systems...'}
              {phase === 'data' && 'Loading Workspace & Site Records...'}
            </span>
          </div>
          {phase === 'data' && (
            <button
              type="button"
              data-testid="unified-loader-escape"
              onClick={() => {
                setDismissed(true);
                setShow(false);
                onEscape?.();
              }}
              className="relative z-20 px-4 py-1.5 rounded border border-border/60 bg-background/80 text-[10px] uppercase tracking-[0.2em] text-text-secondary hover:text-text-primary hover:border-primary/50 transition-colors"
            >
              Continue to Workspace
            </button>
          )}
        </div>

      </div>

      <LoaderStyles />
    </div>
  );
}

// -----------------------------------------------------
// SUB-COMPONENTS FOR ANIMATIONS
// -----------------------------------------------------

function UnifiedAnimation({ current }: { current: Config }) {
  return (
    <>
      {/* LEFT: POWER & COOLING MODULE */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full w-32 shrink-0">
        {/* The reactor CARD is the centred anchor — it lines up horizontally (same centre point) with
            the DC card so the transmission line is level. The cooling module floats ON TOP of the card
            (absolute) so it never pushes the card down. */}
        <div className="relative flex items-center justify-center">
          <div className="absolute left-1/2 -translate-x-1/2 bottom-[calc(100%-2px)]">
            <CoolingModule config={current.cooling} />
          </div>
          <PowerModule config={current.power} />
        </div>

        {/* Labels — anchored a fixed distance below the wrapper centre (= card centre), so BOTH nodes'
            names sit at the same height AND fully clear the cards above (no overlap with the animations). */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 mt-16 z-30 flex flex-col items-center w-48 whitespace-nowrap text-center">
          <span
            key={current.power.id + '-title'}
            className="text-[11px] text-primary font-bold uppercase tracking-[0.2em] drop-shadow-[0_0_8px_rgba(var(--primary-rgb),0.5)]"
            style={{ animation: 'textScramble 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) both' }}
          >
            {current.power.name}
          </span>
          <span
            key={current.power.id + '-status'}
            className="text-[9px] text-primary/60 uppercase tracking-[0.3em] mt-1"
            style={{ animation: 'textScramble 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) both 0.2s' }}
          >
            {current.power.status}
          </span>
        </div>
      </div>

      {/* CENTER: TRANSMISSION LINES */}
      <div className="absolute left-[9rem] right-[9rem] sm:left-[11rem] sm:right-[11rem] top-1/2 -translate-y-1/2 pointer-events-none z-0 flex flex-col items-center justify-center h-24 overflow-hidden">
          <TransmissionModule lines={current.dc.lines} key={current.id + '-trans'} />
      </div>

      {/* RIGHT: DATA CENTER TARGET */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full w-32 shrink-0">
        {/* Card centred so its centre point lines up with the reactor card (level transmission line). */}
        <div className="relative flex items-center justify-center">
          <DataCenterModule config={current.dc} />
        </div>

        {/* Same mt-16 anchor as the power label → both names sit at the same height, clear of the card. */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 mt-16 z-30 flex flex-col items-center w-48 whitespace-nowrap text-center">
          <span
            key={current.dc.id + '-title'}
            className="text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-500"
            style={{ animation: 'receiveText 7s infinite ease-out' }}
          >
            {current.dc.name}
          </span>
          <span
            key={current.dc.id + '-status'}
            className="text-[9px] uppercase tracking-[0.3em] mt-1 transition-colors duration-500"
            style={{ animation: 'receiveSubtext 7s infinite ease-out' }}
          >
            Ingesting
          </span>
        </div>
      </div>
    </>
  );
}

function PowerModule({ config }: { config: Config['power'] }) {
  // We use key={config.id} to force re-mounting and re-trigger entry animations
  return (
    <div key={config.id} className="w-24 h-24 rounded-2xl bg-surface-2 border border-primary/30 flex items-center justify-center relative shadow-[0_0_20px_rgba(var(--primary-rgb),0.1)] [transform-style:preserve-3d] [perspective:800px] z-20">
      {/* Base glow */}
      <div className="absolute inset-0 rounded-2xl bg-primary/10 blur-md animate-pulse" />
      
      {/* Render unique internals based on type */}
      {config.id === 'large-nuclear' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute w-16 h-16 rounded-full border-4 border-primary/20 animate-ping" style={{ animationDuration: '6s' }} />
          <div className="absolute w-12 h-12 rounded-full border-2 border-primary/40 animate-[spin_20s_linear_infinite]" />
          <div className="absolute w-8 h-8 rounded-full bg-primary/80 shadow-[0_0_15px_var(--primary)] animate-[pulse_4s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
          <Hexagon className="w-5 h-5 text-background relative z-10 animate-[spin_8s_linear_infinite]" />
        </div>
      )}

      {config.id === 'smr' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute w-14 h-14 border border-primary/40 rounded-sm animate-[spin_12s_linear_infinite]" />
          <div className="absolute w-14 h-14 border border-primary/40 rounded-sm animate-[spin_12s_linear_infinite_reverse]" />
          <div className="w-6 h-10 flex gap-1">
            <div className="flex-1 bg-primary/80 rounded-sm animate-[pulse_3s_ease-in-out_infinite]" />
            <div className="flex-1 bg-primary/80 rounded-sm animate-[pulse_3s_ease-in-out_infinite_1s]" />
            <div className="flex-1 bg-primary/80 rounded-sm animate-[pulse_3s_ease-in-out_infinite_2s]" />
          </div>
        </div>
      )}

      {config.id === 'microreactor' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Hexagon className="w-12 h-12 text-primary/30 absolute animate-[spin_6s_linear_infinite]" />
          <div className="w-6 h-6 bg-primary rounded-full shadow-[0_0_20px_var(--primary)] flex items-center justify-center">
             <Activity className="w-4 h-4 text-background animate-[pulse_4s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
          </div>
        </div>
      )}

      {config.id === 'ccgt' && (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-2xl">
          <Fan className="w-16 h-16 text-primary/40 absolute animate-[spin_1s_linear_infinite]" />
          <div className="w-8 h-8 rounded-full border-4 border-primary/80 shadow-[0_0_10px_var(--primary)]" />
          <div className="absolute -right-2 w-4 h-full flex flex-col justify-around py-2">
             <div className="h-1 bg-info/60 w-full animate-[powerSurge_2s_infinite]" />
             <div className="h-1 bg-primary/60 w-full animate-[powerSurge_2s_infinite_0.4s]" />
          </div>
        </div>
      )}

      {config.id === 'recip-gas' && (
        <div className="absolute inset-0 flex items-center justify-center gap-1.5 px-3">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="flex-1 h-12 bg-surface-1 border border-primary/30 rounded-full relative overflow-hidden">
              <div 
                className="absolute bottom-0 w-full bg-primary/80 rounded-full"
                style={{ 
                  height: '50%',
                  animation: `piston 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite alternate`,
                  animationDelay: `${i * 0.2}s`
                }} 
              />
            </div>
          ))}
        </div>
      )}

      {config.id === 'solar-bess' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          <div className="flex items-center gap-2">
            <Sun className="w-6 h-6 text-amber-400 animate-[spin_20s_linear_infinite]" />
            <div className="w-8 h-2 bg-surface-1 rounded-sm overflow-hidden border border-amber-400/30">
               <div className="h-full bg-amber-400/80 animate-[solarFill_4s_infinite]" />
            </div>
          </div>
          <div className="flex items-center gap-1">
             <Battery className="w-6 h-6 text-primary" />
             <div className="w-10 h-3 bg-surface-1 rounded-sm border border-primary/50 flex items-center p-[1px] gap-[1px]">
               <div className="h-full flex-1 bg-primary animate-[pulse_2s_infinite]" />
               <div className="h-full flex-1 bg-primary animate-[pulse_2s_infinite_0.4s]" />
               <div className="h-full flex-1 bg-primary animate-[pulse_2s_infinite_0.8s]" />
             </div>
          </div>
        </div>
      )}

      {config.id === 'fuel-cell' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Layers className="w-12 h-12 text-primary/25 absolute animate-[pulse_4s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
          <div className="flex flex-col gap-[3px] z-10">
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className="w-9 h-1.5 bg-primary/80 rounded-sm animate-[pulse_2s_infinite]"
                style={{ animationDelay: `${i * 0.25}s` }}
              />
            ))}
          </div>
        </div>
      )}

      {config.id === 'wind-bess' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          <Wind className="w-7 h-7 text-info animate-[spin_3s_linear_infinite]" />
          <div className="flex items-center gap-1">
            <Battery className="w-6 h-6 text-primary" />
            <div className="w-10 h-3 bg-surface-1 rounded-sm border border-primary/50 flex items-center p-[1px] gap-[1px]">
              <div className="h-full flex-1 bg-primary animate-[pulse_2s_infinite]" />
              <div className="h-full flex-1 bg-primary animate-[pulse_2s_infinite_0.4s]" />
              <div className="h-full flex-1 bg-primary animate-[pulse_2s_infinite_0.8s]" />
            </div>
          </div>
        </div>
      )}

      {/* Holographic Entry Animation Overlay */}
      <div className="absolute inset-0 z-30 pointer-events-none rounded-2xl" style={{ animation: 'holoEntry 1.4s cubic-bezier(0.34, 1.56, 0.64, 1) both' }} />
    </div>
  );
}

/** A cooling icon, vertically centred in its slot (equal space above + below), with a small grey
 *  stand-line drawn just beneath it (absolute, so it never shifts the icon off centre). */
function MountedCoolIcon({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex items-center justify-center">
      {children}
      <span className="absolute top-full left-1/2 -translate-x-1/2 w-[3px] h-1.5 bg-border-subtle rounded-full" aria-hidden="true" />
    </div>
  );
}

function CoolingModule({ config }: { config: Config['cooling'] }) {
  // Attached module sitting on top of the Power Module. Icons spread evenly (justify-around) and each
  // is mounted on its own centered grey stand-line (drawn behind it), instead of clustering the icons
  // in the centre while two fixed pipes sat off to the sides + on top.
  return (
    <div key={config.id} className="h-10 w-16 -mb-[2px] bg-surface-1 border border-border rounded-t-xl relative z-30 overflow-hidden flex items-center justify-around px-1.5">

      {config.type === 'wet' && (
        <div className="flex items-end gap-1 self-end pb-1">
          <div className="w-1 bg-info/60 rounded-full animate-[vaporRise_3s_infinite_ease-out]" style={{ height: '6px' }} />
          <div className="w-1.5 bg-info/80 rounded-full animate-[vaporRise_4s_infinite_ease-out_0.6s]" style={{ height: '12px' }} />
          <div className="w-1 bg-info/60 rounded-full animate-[vaporRise_2.4s_infinite_ease-out_1.2s]" style={{ height: '8px' }} />
        </div>
      )}

      {config.type === 'dry' && (
        <>
          <MountedCoolIcon><Fan className="w-4 h-4 text-text-secondary animate-[spin_0.6s_linear_infinite]" /></MountedCoolIcon>
          <MountedCoolIcon><Fan className="w-4 h-4 text-text-secondary animate-[spin_0.6s_linear_infinite_reverse]" /></MountedCoolIcon>
        </>
      )}

      {config.type === 'hybrid' && (
        <>
          <MountedCoolIcon><Droplet className="w-3.5 h-3.5 text-info animate-[pulse_4s_cubic-bezier(0.4,0,0.6,1)_infinite]" /></MountedCoolIcon>
          <MountedCoolIcon><Fan className="w-4 h-4 text-primary animate-[spin_1s_linear_infinite]" /></MountedCoolIcon>
        </>
      )}
    </div>
  );
}

function TransmissionModule({ lines }: { lines: number }) {
  return (
    <div className="w-full h-full relative flex flex-col justify-center gap-2 py-4">
      {/* The physical wires container */}
      {Array.from({ length: lines }).map((_, i) => {
        const speed = 3.0 + (Math.random() * 3.0); // Randomize speeds between 3.0s and 6.0s (was 1.5s - 3s)
        const delay = Math.random() * 2;
        const color = i % 2 === 0 ? 'var(--primary)' : 'var(--info)';
        
        return (
          <div key={i} className="w-full h-[2px] bg-border-subtle/20 relative rounded-full">
            {/* Surge line */}
            <div 
              className="absolute left-0 top-0 h-full w-[40%] blur-[2px]"
              style={{ 
                background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
                animation: `powerSurge ${speed}s infinite cubic-bezier(0.4, 0, 0.2, 1) ${delay}s` 
              }}
            />
            {/* Fast photon */}
            <div 
              className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] w-6 rounded-full"
              style={{ 
                backgroundColor: color,
                boxShadow: `0 0 10px 2px ${color}`,
                animation: `particleShoot ${speed * 0.8}s infinite cubic-bezier(0.3, 0, 0.1, 1) ${delay + 0.4}s` 
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

function DataCenterModule({ config }: { config: Config['dc'] }) {
  return (
    <div key={config.id} className="w-24 h-28 rounded-xl bg-surface-2 border border-border flex items-center justify-center relative overflow-hidden transition-all shadow-md z-20" style={{ animation: 'receivePulse 7s infinite ease-out' }}>
      
      {/* Scanning laser line inside the server box */}
      <div className="absolute left-0 right-0 h-[2px] bg-info/40 blur-[1px] z-0" style={{ animation: 'scan 5s cubic-bezier(0.4, 0, 0.2, 1) infinite' }} />
      
      <div className="w-full h-full p-2 flex flex-col gap-1 z-10 relative" style={{ animation: 'holoEntry 1s ease-out both' }}>
        
        {config.type === 'hyperscale' && (
          // Dense rows of servers
          <div className="flex-1 w-full flex flex-wrap gap-1 content-start">
            {Array.from({ length: 16 }).map((_, i) => (
              <div key={i} className="w-[calc(50%-2px)] h-2.5 bg-surface-1 border border-border rounded-sm relative overflow-hidden">
                <div className="absolute left-1 top-1 w-1 h-1 rounded-full bg-info animate-ping" style={{ animationDelay: `${i * 0.2}s`, animationDuration: '4s' }} />
              </div>
            ))}
          </div>
        )}

        {config.type === 'colocation' && (
          // Segmented cages
          <div className="flex-1 w-full flex flex-col gap-1.5 justify-center">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-4 w-full bg-surface-1 border border-border rounded-sm flex items-center justify-between px-1">
                 <div className="flex gap-0.5">
                   <div className="w-1 h-2 bg-text-muted/30" />
                   <div className="w-1 h-2 bg-text-muted/30" />
                 </div>
                 <div className={`w-1.5 h-1.5 rounded-full ${i % 2 === 0 ? 'bg-primary' : 'bg-info'} animate-[pulse_4s_cubic-bezier(0.4,0,0.6,1)_infinite]`} style={{ animationDelay: `${i * 0.6}s` }} />
              </div>
            ))}
          </div>
        )}

        {config.type === 'edge' && (
          // Singular robust rack
          <div className="flex-1 w-full flex items-center justify-center">
            <div className="w-12 h-16 bg-surface-1 border-2 border-border-strong rounded-md flex flex-col justify-around p-1 shadow-inner relative">
              <div className="w-full h-1 bg-border rounded-full" />
              <div className="w-full h-1 bg-border rounded-full" />
              <div className="flex justify-between items-center w-full">
                <div className="w-4 h-1 bg-info/80 animate-[pulse_4s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
                <div className="w-2 h-2 rounded-full bg-primary animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
              </div>
              <div className="w-full h-1 bg-border rounded-full" />
            </div>
          </div>
        )}

      </div>

      {/* Telemetry Spectrum Visualizer at bottom */}
      <div className="absolute bottom-0 left-0 w-full h-4 bg-background/50 backdrop-blur-sm border-t border-border-subtle flex items-end justify-center gap-[2px] p-0.5 z-20">
        {[0,1,2,3,4,5,6,7].map(i => (
          <div 
            key={i} 
            className="w-[3px] bg-info rounded-t-sm" 
            style={{ 
              animation: 'telemetry 4s infinite ease-in-out', 
              animationDelay: `${i * 0.3}s` 
            }} 
          />
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------
// STYLES
// -----------------------------------------------------

function LoaderStyles() {
  return (
    <style dangerouslySetInnerHTML={{__html: `
      @keyframes shine {
        to { background-position: -200% center; }
      }
      
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      @keyframes holoEntry {
        0% { transform: perspective(400px) rotateY(-90deg) scale(0.8); opacity: 0; filter: blur(4px); }
        50% { transform: perspective(400px) rotateY(10deg) scale(1.05); opacity: 1; filter: blur(1px); }
        100% { transform: perspective(400px) rotateY(0deg) scale(1); opacity: 1; filter: blur(0px); }
      }

      @keyframes textScramble {
        0% { opacity: 0; transform: translateY(4px) scale(0.95); letter-spacing: -0.05em; filter: blur(2px); color: var(--info); }
        40% { opacity: 1; transform: translateY(-1px) scale(1.02); letter-spacing: 0.3em; filter: blur(0px); color: color-mix(in srgb, var(--primary) 50%, white); }
        100% { transform: translateY(0) scale(1); letter-spacing: 0.2em; color: var(--primary); }
      }

      @keyframes scan {
        0%, 60% { top: -10%; opacity: 0; }
        65% { opacity: 1; }
        85% { top: 110%; opacity: 0; }
        100% { top: 110%; opacity: 0; }
      }

      @keyframes gridDrift {
        0% { transform: translateY(0); }
        100% { transform: translateY(-48px); }
      }

      @keyframes telemetry {
        0%, 50%, 95%, 100% { height: 20%; opacity: 0.2; }
        75% { height: 100%; opacity: 1; }
      }

      @keyframes receivePulse {
        0%, 50%, 95%, 100% { box-shadow: 0 0 10px -5px transparent; border-color: var(--border); transform: scale(1); }
        75% { box-shadow: 0 0 40px 8px color-mix(in srgb, var(--info) 30%, transparent); border-color: var(--info); transform: scale(1.02); }
      }

      @keyframes receiveText {
        0%, 50%, 95%, 100% { color: var(--text-muted); text-shadow: none; }
        75% { color: var(--info); text-shadow: 0 0 10px color-mix(in srgb, var(--info) 60%, transparent); font-weight: 800; }
      }

      @keyframes receiveSubtext {
        0%, 50%, 95%, 100% { color: color-mix(in srgb, var(--text-muted) 50%, transparent); text-shadow: none; }
        75% { color: var(--info); text-shadow: 0 0 8px var(--info); }
      }

      @keyframes powerSurge {
        0% { transform: translateX(-150%); opacity: 0; }
        20% { opacity: 1; }
        80% { opacity: 1; }
        100% { transform: translateX(250%); opacity: 0; }
      }

      @keyframes particleShoot {
        0% { transform: translateX(-200%); opacity: 0; }
        5% { opacity: 1; }
        85% { opacity: 1; }
        100% { transform: translateX(500%); opacity: 0; }
      }

      @keyframes piston {
        0%, 20% { height: 20%; }
        80%, 100% { height: 90%; }
      }

      @keyframes vaporRise {
        0% { transform: translateY(10px) scale(0.8); opacity: 0; }
        50% { opacity: 1; }
        100% { transform: translateY(-15px) scale(1.2); opacity: 0; }
      }

      @keyframes solarFill {
        0% { width: 0%; opacity: 0.5; }
        50% { width: 100%; opacity: 1; }
        100% { width: 100%; opacity: 0; }
      }
    `}} />
  );
}
